<script setup lang="ts">
import type { ErrorData } from "@anggunpraya-templates/types";
import type { NuxtError } from "#app";

const props = defineProps({
  error: Object as () => NuxtError<ErrorData & {
    redirect?: string;
  }>,
});

const { coupleName } = storeToRefs(useTemplateStore());

const errorProps = computed(() => {
  const message = props.error?.data?.message || props.error?.message;
  return {
    status: props.error?.data?.status || props.error?.statusText,
    code: props.error?.data?.code || props.error?.status,
    message: message?.toLowerCase().includes("page not found") ? "Halaman tidak ditemukan" : message || "Terjadi kesalahan",
  };
});

const isPrivateInvitationError = computed(() => {
  return errorProps.value.message === "Mohon maaf, undangan yang Anda akses bersifat pribadi.";
});

const errorTitle = computed(() => isPrivateInvitationError.value ? "Undangan Pribadi" : errorProps.value.message);
const errorDescription = computed(() => {
  if (isPrivateInvitationError.value) {
    return "Undangan ini bersifat pribadi dan tidak untuk disebarluaskan. Jika Anda menerima undangan ini, harap konfirmasi melalui tautan resmi dari pengantin.";
  }

  return "Mohon maaf, halaman undangan tidak dapat ditampilkan. Silakan kembali ke awal atau hubungi Anggun Praya jika kendala masih terjadi.";
});

function onBackHome() {
  clearError({
    redirect: "/",
  });
}

watch(
  () => errorProps.value,
  (newError) => {
    if (isPrivateInvitationError.value) {
      useHead({
        title: `Undangan Pribadi - ${coupleName.value}`,
      });
    }
    else {
      useHead({
        title: `${newError.message} - ${coupleName.value}`,
      });
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div class="ap-006-safe-min-screen relative flex overflow-hidden bg-primary-950 text-white">
    <main class="ap-006-safe-min-screen relative flex w-full items-center justify-center px-7 py-20 text-center sm:px-10">
      <NuxtImg
        src="/hero.webp"
        alt="Foto latar halaman error Deva dan Rifqah"
        class="absolute inset-0 size-full object-cover object-[52%_center] opacity-68"
        loading="eager"
        decoding="async"
        fetchpriority="high"
        :preload="{
          fetchPriority: 'high',
        }"
      />
      <div class="absolute inset-0 bg-[radial-gradient(circle_at_50%_34%,rgba(255,255,255,0.16),transparent_34%),linear-gradient(180deg,rgba(0,0,0,0.66)_0%,rgba(0,0,0,0.46)_42%,rgba(0,0,0,0.86)_100%)]" />
      <div class="pointer-events-none absolute inset-x-0 top-0 h-32 bg-[linear-gradient(180deg,rgba(0,0,0,0.88),rgba(0,0,0,0))]" />
      <div class="pointer-events-none absolute inset-x-0 bottom-0 h-44 bg-[linear-gradient(0deg,#000,rgba(0,0,0,0))]" />

      <section
        class="ap-006-glass relative z-1 mx-auto flex w-full max-w-[34rem] flex-col items-center rounded-[1.25rem] px-6 py-8 shadow-[0_28px_90px_rgba(0,0,0,0.48)] sm:px-8 sm:py-10"
        aria-labelledby="error-title"
      >
        <p class="text-kicker">
          The Wedding Of
        </p>

        <h1 class="text-panel-title mt-4 text-4xl! sm:text-5xl!">
          {{ coupleName }}
        </h1>

        <div class="my-7 h-px w-24 bg-gradient-to-r from-transparent via-white/64 to-transparent shadow-[0_0_14px_rgba(255,255,255,0.42)]" />

        <p class="font-sans text-xs font-semibold uppercase tracking-[0.28em] text-white/62">
          {{ errorProps.code || errorProps.status || 'Error' }}
        </p>

        <h2
          id="error-title"
          class="text-section-title mt-3"
        >
          {{ errorTitle }}
        </h2>

        <p class="text-copy mx-auto mt-5 max-w-md font-sans! text-white/86">
          {{ errorDescription }}
        </p>

        <div class="mt-8 flex w-full flex-col items-center justify-center gap-3 xs:flex-row">
          <UButton
            variant="glow"
            icon="i-lucide-arrow-left"
            class="w-full justify-center rounded-xl font-bold xs:w-auto"
            @click="onBackHome"
          >
            Kembali
          </UButton>

          <UButton
            to="https://anggunpraya.id?utm_source=ap-006&utm_medium=error_page&utm_campaign=template_support&utm_content=error_cta"
            target="_blank"
            rel="noopener noreferrer"
            external
            variant="glass"
            icon="i-lucide-message-circle"
            class="w-full justify-center rounded-xl font-bold xs:w-auto"
          >
            Hubungi Kami
          </UButton>
        </div>

        <footer class="mt-8">
          <NuxtLink
            class="inline-flex h-auto w-auto items-center justify-center opacity-90 transition hover:opacity-100"
            href="https://anggunpraya.id?utm_source=ap-006&utm_medium=error_page&utm_campaign=template_credit&utm_content=error_logo"
            target="_blank"
            rel="noopener noreferrer"
            external
          >
            <UiLogoBrand
              variant="light"
              alt="Anggun Praya"
              class="w-45 h-auto md:w-49 xl:w-52"
            />
          </NuxtLink>
        </footer>
      </section>
    </main>
  </div>
</template>
